import Vue from "vue";
import ApiService from "../../services/ApiService";
import { EventBus, EVENTS } from "../../services/EventBus";
import { mapGetters } from "vuex";

Vue.component("rumor-probability-widget", {
  delimiters: ["${", "}"],

  props: {
    variationId: {
      type: Number,
      required: true,
    },
    showTitle: {
      type: Boolean,
      default: true,
    },
    title: {
      type: String,
      default: "Wie wahrscheinlich ist dieses Gerücht?",
    },
    // Neue Props für initiale Daten aus Item-Properties
    initialAverageProbability: {
      type: Number,
      default: null,
    },
    initialCount: {
      type: Number,
      default: null,
    },
    // Deaktiviert Abstimmungen (z.B. bei geschlossenen Gerüchten)
    disabled: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      averageProbability: 0,
      displayedProbability: 0, // Für Animation
      count: 0,
      userProbability: null,
      selectedProbability: 50,
      isLoading: false,
      message: "",
      showAverageTooltip: false,
      showMinTooltip: false,
      showMaxTooltip: false,
      animatingProbability: false,
    };
  },

  computed: {
    ...mapGetters(["isLoggedIn"]),
    
    hasVoted() {
      return this.userProbability !== null;
    },
    probabilityText() {
      if (this.averageProbability === 0 && this.count === 0) {
        return "Noch keine";
      }
      // Verwende displayedProbability für Animation, auf ganze Zahl gerundet
      return `${this.displayedProbability.toFixed(0)}%`;
    },
    // Farbberechnung von 0 (blau) bis 100 (rot)
    averageColor() {
      // Verwende displayedProbability für Animation
      return this.getProbabilityColor(this.displayedProbability);
    },
    selectedColor() {
      return this.getProbabilityColor(this.selectedProbability);
    },
  },

  watch: {
    isLoggedIn(newVal) {
      if (newVal && this.initialAverageProbability !== null) {
        // User hat sich eingeloggt, lade seine Einschätzung
        this.loadUserProbability();
      }
    },
  },

  created() {    
  },

  mounted() {
    // Only load in browser, not during SSR
    if (typeof window !== "undefined") {
      // Prüfe ob initiale Daten als Props übergeben wurden
      if (this.initialAverageProbability !== null) {
        // Nutze die übergebenen Daten, KEIN API-Call nötig
        this.averageProbability = this.initialAverageProbability;
        this.displayedProbability = this.initialAverageProbability;
        this.count = this.initialCount || 0;
        
        // Wenn User eingeloggt ist, lade seine persönliche Einschätzung
        // (für Slider-Position und "Aktualisieren"-Button)
        if (this.isLoggedIn) {
          this.loadUserProbability();
        }
      } else {
        // Keine initialen Daten vorhanden (sehr selten), lade alles via API
        this.loadStats();
      }
    }
  },

  methods: {
    loadStats() {
      this.isLoading = true;

      ApiService.get("/rest/fanliebling/rumor-probability", {
        variationId: this.variationId,
      })
        .done((response) => {
          const data = response.data || response;
          this.averageProbability = data.averageProbability || 0;
          this.count = data.count || 0;
          this.userProbability = data.userProbability;

          // Initiales displayedProbability setzen
          this.displayedProbability = this.averageProbability;

          // Wenn User bereits gevotet hat, setze Slider auf seinen Wert
          if (this.userProbability !== null) {
            this.selectedProbability = this.userProbability;
          }
        })
        .fail((error, status) => {
          // Stiller Fehler - keine Notification nötig
        })
        .always(() => {
          this.isLoading = false;
        });
    },

    // Lädt nur die User-spezifische Wahrscheinlichkeit (für optimierte Performance)
    loadUserProbability() {
      if (!this.isLoggedIn) {
        return;
      }

      this.isLoading = true;

      ApiService.get("/rest/fanliebling/rumor-probability", {
        variationId: this.variationId,
      })
        .done((response) => {
          const data = response.data || response;
          this.userProbability = data.userProbability;
          
          // Wenn User bereits gevotet hat, setze Slider auf seinen Wert
          if (this.userProbability !== null) {
            this.selectedProbability = this.userProbability;
          }
        })
        .fail((error, status) => {
          // Stiller Fehler - keine Notification nötig
        })
        .always(() => {
          this.isLoading = false;
        });
    },

    openLoginModal() {
      // Öffne das Login Modal
      const loginButton = document.querySelector('.control-user a[data-toggle="modal"]');
      if (loginButton) {
        loginButton.click();
      }
    },

    submitProbability() {
      if (this.isLoading || this.disabled) {
        return;
      }

      if (!this.isLoggedIn) {
        this.openLoginModal();
        return;
      }

      this.isLoading = true;
      this.message = "";

      ApiService.post("/rest/fanliebling/rumor-probability", {
        variationId: this.variationId,
        probability: this.selectedProbability,
      }, {
        supressNotifications: true // Unterdrücke globale Fehler-Notifications
      })
        .done((response) => {
          const data = response.data || response;

          if (data && data.averageProbability !== undefined) {
            const oldProbability = this.averageProbability;
            const newProbability = parseFloat(data.averageProbability) || 0;
            
            // Aktualisiere alle Werte aus der Response
            this.$set(this, "averageProbability", newProbability);
            this.$set(this, "count", parseInt(data.count) || 0);
            this.$set(this, "userProbability", parseInt(data.userProbability));

            // Animiere den Wert hochzählen
            this.animateValue(oldProbability, newProbability, 800);

            this.message = "Vielen Dank für deine Einschätzung!";

            // Event auslösen für Badge-Update
            EventBus.$emit(EVENTS.INTERACTION_COMPLETED);

            // Nachricht nach 3 Sekunden ausblenden
            setTimeout(() => {
              this.message = "";
            }, 3000);
          } else {
            this.message = "Ein Fehler ist aufgetreten.";
          }
        })
        .fail((error) => {
          // Fehlerbehandlung - User kann es nochmal versuchen
          this.message = "Fehler beim Speichern. Bitte versuche es erneut.";
          setTimeout(() => {
            this.message = "";
          }, 3000);
        })
        .always(() => {
          this.isLoading = false;
        });
    },

    updateSliderValue(event) {
      this.selectedProbability = parseInt(event.target.value);
    },
    
    // Animiert einen Wert von start zu end über eine bestimmte Dauer
    animateValue(start, end, duration) {
      this.animatingProbability = true;
      const startTime = performance.now();
      const difference = end - start;
      
      const animate = (currentTime) => {
        const elapsed = currentTime - startTime;
        const progress = Math.min(elapsed / duration, 1);
        
        // Easing function (ease out)
        const easeProgress = 1 - Math.pow(1 - progress, 3);
        
        this.displayedProbability = start + (difference * easeProgress);
        
        if (progress < 1) {
          requestAnimationFrame(animate);
        } else {
          this.displayedProbability = end;
          this.animatingProbability = false;
        }
      };
      
      requestAnimationFrame(animate);
    },

    // Berechnet die Farbe basierend auf der Wahrscheinlichkeit (0 = blau, 100 = rot)
    getProbabilityColor(probability) {
      if (probability === 0 && this.count === 0) {
        return "#6c757d"; // Grau für "keine Daten"
      }

      // Farben: Blau (0, 123, 255) -> Rot (220, 53, 69)
      const percent = probability / 100;
      
      // Direkte Interpolation von Blau zu Rot
      const r = Math.round(0 + (220 - 0) * percent);
      const g = Math.round(123 + (53 - 123) * percent);
      const b = Math.round(255 + (69 - 255) * percent);
      
      return `rgb(${r}, ${g}, ${b})`;
    },
  },

  render: function(h) {
    return h("div", { class: "rumor-probability-widget mb-3" }, [
      // Headline
      h("div", { 
        class: "widget-headline my-2 text-center font-weight-bold",        
      }, "Wie wahrscheinlich ist der Transfer?"),
      
      // Durchschnittsanzeige mit Anzahl in Klammern
      h("div", { 
        class: "d-flex justify-content-between align-items-center",
        style: { fontSize: "0.85rem" }
      }, [
        h("span", { 
          style: { fontWeight: "600" }
        }, "Eure Einschätzung:"),
        h("span", { 
          class: "d-flex align-items-center mb-0",
          style: {            
            color: this.averageColor,
            fontWeight: "700",
            fontSize: "1.5rem",
          }
        }, [
          this.probabilityText,
          this.count > 0
            ? h("small", { 
                class: "ml-1",
                style: { fontSize: "0.85rem", fontWeight: "400" }
              }, `(${this.count})`)
            : null,
        ]),
      ]),

      // Success Message
      this.message
        ? h("div", { 
            class: "alert alert-success mb-2 py-2 px-3",
            style: { fontSize: "0.85rem" }
          }, this.message)
        : null,

      // Kompakter Slider mit inline Wert
      h("div", { class: "mb-2" }, [
        h("div", { 
          class: "d-flex align-items-center",
          style: { gap: "12px" }
        }, [
          // 0% mit Tooltip
          h("div", {
            class: "position-relative",
            style: { minWidth: "30px", cursor: "help" },
            on: {
              mouseenter: () => { this.showMinTooltip = true; },
              mouseleave: () => { this.showMinTooltip = false; }
            }
          }, [
            h("span", { 
              style: { 
                fontSize: "0.75rem",
                fontWeight: "600",
                color: "var(--color-text-secondary)"
              }
            }, "0%"),
            // this.showMinTooltip
            //   ? h("div", {
            //       class: "custom-tooltip-probability",
            //       style: {
            //         position: "absolute",
            //         bottom: "calc(100% + 6px)",
            //         left: "50%",
            //         transform: "translateX(-50%)",
            //         whiteSpace: "nowrap",
            //         zIndex: "1000"
            //       }
            //     }, "Unwahrscheinlich")
            //   : null
          ]),
          
          // Slider
          h("input", {
            class: "rumor-slider flex-grow-1",
            style: {
              opacity: this.isLoading || this.disabled ? "0.5" : "1",
              backgroundImage: `linear-gradient(to right, rgb(0, 123, 255) 0%, ${this.selectedColor} ${this.selectedProbability}%, #e9ecef ${this.selectedProbability}%, #e9ecef 100%)`,
            },
            attrs: {
              type: "range",
              min: "0",
              max: "100",
              step: "1",
              value: this.selectedProbability,
              disabled: this.isLoading || this.disabled,
            },
            on: {
              input: this.updateSliderValue,
            },
          }),
          
          // 100% mit Tooltip
          h("div", {
            class: "position-relative",
            style: { minWidth: "50px", cursor: "help" },
            on: {
              mouseenter: () => { this.showMaxTooltip = true; },
              mouseleave: () => { this.showMaxTooltip = false; }
            }
          }, [
            h("span", { 
              style: { 
                fontSize: "1.25rem",
                fontWeight: "700",
                color: this.selectedColor,
                textAlign: "center",
                display: "block"
              }
            }, `${this.selectedProbability}%`),
            // this.showMaxTooltip
            //   ? h("div", {
            //       class: "custom-tooltip-probability",
            //       style: {
            //         position: "absolute",
            //         bottom: "calc(100% + 6px)",
            //         right: "0",
            //         whiteSpace: "nowrap",
            //         zIndex: "1000"
            //       }
            //     }, "Sehr wahrscheinlich")
            //   : null
          ]),
        ]),
      ]),

      // Kompakter Submit Button
      h(
        "button",
        {
          class: {
            "btn": true,
            "btn-primary": true,
            "btn-block": true,
            "btn-sm": true,
          },
          style: {
            fontSize: "0.85rem",
            padding: "0.5rem 1rem",
          },
          attrs: {
            disabled: this.isLoading || this.disabled,
          },
          on: {
            click: this.submitProbability,
          },
        },
        [
          this.isLoading
            ? h("span", [
                h("i", { class: "fa fa-spinner fa-spin mr-1" }),
                "Speichern...",
              ])
            : this.hasVoted
            ? "Aktualisieren"
            : "Abgeben",
        ]
      ),
    ]);
  },
});

export default {};
